import { isPlatformBrowser } from '@angular/common';
import {
    Directive,
    effect,
    inject,
    input,
    NgZone,
    OnDestroy,
    PLATFORM_ID,
} from '@angular/core';

@Directive({
    selector: '[dynamicHeaderPage]',
    standalone: true,
})
export class DynamicHeaderPageDirective implements OnDestroy {
    pageDataLoaded = input<boolean>(false);
    dynamicHeaderPage = input<boolean>(false);
    excludedHeaders = input<string[]>([]);

    private readonly platformId = inject(PLATFORM_ID);
    private readonly isBrowser = isPlatformBrowser(this.platformId);
    private readonly ngZone = inject(NgZone);

    private boundScrollHandler?: () => void;
    private modeResolved = false;
    private lastMode: boolean | null = null;
    private appliedOnce = false;

    constructor() {
        if (!this.isBrowser) return;

        this.initBaseScroll();
        effect(() => {
            const isDynamic = this.dynamicHeaderPage();
            const loaded = this.pageDataLoaded();

            if (!loaded) return;

            // permite re-ejecutar en primera carga aunque el valor no cambie
            if (this.appliedOnce && this.lastMode === isDynamic) return;

            this.lastMode = isDynamic;
            this.appliedOnce = true;
            this.modeResolved = true;

            this.ensureApplied(isDynamic);
        });
    }

    ngOnDestroy(): void {
        if (!this.isBrowser) return;

        if (this.boundScrollHandler) {
            document.removeEventListener(
                'scroll',
                this.boundScrollHandler,
                true
            );
            window.removeEventListener('wheel', this.boundScrollHandler);
        }
    }

    private ensureApplied(isDynamic: boolean) {
        let attempts = 0;

        const tryApply = () => {
            const header = document.querySelector('.mdl-header');
            const container = header;

            // CLAVE: esperar también a que tenga altura (DOM ya pintado)
            const isReady =
                header &&
                container &&
                (container as HTMLElement).offsetHeight > 0;

            if (isReady) {
                this.ngZone.run(() => {
                    this.applyMode(isDynamic);
                });

                // RE-APLICACIÓN FINAL (esto arregla el 1st paint bug)
                setTimeout(() => {
                    this.applyMode(this.dynamicHeaderPage());
                }, 0);

                return;
            }

            if (attempts < 20) {
                attempts++;
                requestAnimationFrame(tryApply);
            }
        };

        requestAnimationFrame(tryApply);
    }

    private initBaseScroll(): void {
        const hasTopSlider = this.hasTopSlider();

        this.boundScrollHandler = () => {
            this.ngZone.run(() => {
                if (this.modeResolved) {
                    this.manageScroll(hasTopSlider);
                }
            });
        };

        document.addEventListener('scroll', this.boundScrollHandler, true);
        window.addEventListener('wheel', this.boundScrollHandler, {
            passive: true,
        });

        effect(() => {
            const isDynamic = this.dynamicHeaderPage();

            if (this.lastMode === isDynamic) return;

            this.lastMode = isDynamic;
            this.modeResolved = true;

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.ngZone.run(() => {
                        this.applyMode(isDynamic);
                    });
                });
            });
        });
    }

    private applyMode(isDynamic: boolean): void {
        const hasTopSlider = this.hasTopSlider();

        // RESET
        document.body.classList.remove('header_sticky');

        document.querySelectorAll('.mdl-header')?.forEach((el) => {
            el.classList.remove('sticky');
            (el as HTMLElement).style.backgroundColor = '';
            (el as HTMLElement).style.opacity = '';
        });

        const header = document.querySelector('.mdl-header') as HTMLElement;

        if (header) {
            header.style.opacity = '';
            header.style.backgroundColor = '';
        }

        this.applyHeaderClasses();

        if (isDynamic) {
            this.initDynamicMode(hasTopSlider);
        } else {
            this.initStaticMode();
        }

        this.manageScroll(hasTopSlider);
    }

    private initDynamicMode(hasTopSlider: boolean): void {
        const header = document.querySelector('.mdl-header') as HTMLElement;

        if (header) {
            header.style.opacity = '1';
        }

        this.manageScroll(hasTopSlider);
    }

    private initStaticMode(): void {
        document.body.classList.add('header_sticky');

        document.querySelectorAll('.mdl-header')?.forEach((el) => {
            el.classList.add('sticky');
        });

        const header = document.querySelector('.mdl-header') as HTMLElement;

        if (header) {
            header.style.opacity = '1';
            header.style.backgroundColor = '#ffffff';
        }
    }

    private manageScroll(hasTopSlider: boolean): void {
        if (!this.modeResolved) return;

        const isDynamic = this.dynamicHeaderPage();

        const threshold = isDynamic ? 100 : 0;
        const scrollY =
            window.scrollY || document.documentElement.scrollTop || 0;

        if (scrollY >= threshold) {
            document.body.classList.add('header_sticky');

            document
                .querySelectorAll('.mdl-header')
                ?.forEach((el) => el.classList.add('sticky'));
        } else {
            if (isDynamic) {
                document.body.classList.remove('header_sticky');

                document
                    .querySelectorAll('.mdl-header')
                    ?.forEach((el) => el.classList.remove('sticky'));
            }
        }

        if (window.innerWidth >= 1024) {
            const el = document.getElementById('babylon__gallery-slider');
            const position = (el?.offsetTop ?? 200) - 200;

            const gallery = document.querySelector(
                '.babylon__gallery-slider .babylon__gallery-slider--content'
            );

            if (!gallery) return;

            window.scrollY >= position
                ? gallery.classList.remove('no-active')
                : gallery.classList.add('no-active');
        }
    }

    private hasTopSlider(): boolean {
        return (
            !!document.querySelector('.babylon__top-slider') ||
            !!document.querySelector('lib-babylon-top-slider')
        );
    }

    private applyHeaderClasses(): void {
        if (document.querySelector('#headerCenterBookShow')) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('header-center-bookshow-engine');
        }

        if (document.querySelector('#headerBookShow')) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('header-bookshow-engine');
        }
    }
}
